<script setup>
import { Modal } from 'bootstrap';
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import $http from '@/axios.config';
import Swal from 'sweetalert2';

const store = useStore();
const { t } = useI18n();

const roles = ref([]);
const activeStatus = ref({ name: '', color: '#808080', role: '' });
const isEditMode = computed(() => !!activeStatus.value._id);

const statuses = computed(() => store.getters.allStatuses);

onMounted(async () => {
  await store.dispatch('fetchStatuses');
  const res = await $http.get('/api/users/roles');
  roles.value = res.data;
});

function openModal(status = null) {
  if (status) {
    activeStatus.value = { ...status, role: status.role._id };
  } else {
    activeStatus.value = { name: '', color: '#808080', role: roles.value[0]?._id || '' };
  }
  const modal = new Modal(document.getElementById('statusModal'));
  modal.show();
}

function closeModal() {
  const modal = Modal.getInstance(document.getElementById('statusModal'));
  modal.hide();
}

async function saveStatus() {
  try {
    if (isEditMode.value) {
      await $http.put(`/api/statuses/${activeStatus.value._id}`, activeStatus.value);
      Swal.fire(t('alerts.success'), t('messages.statusUpdated'), 'success');
    } else {
      await $http.post('/api/statuses', activeStatus.value);
      Swal.fire(t('alerts.success'), t('messages.statusCreated'), 'success');
    }
    await store.dispatch('fetchStatuses');
    closeModal();
  } catch (err) {
    Swal.fire(t('alerts.error'), err.response?.data?.message || t('alerts.error'), 'error');
  }
}

async function deleteStatus(statusId) {
  const result = await Swal.fire({
    title: t('alerts.warning'),
    text: t('asks.delete_status'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.delete'),
    cancelButtonText: t('buttons.cancel')
  });

  if (result.isConfirmed) {
    try {
      await $http.delete(`/api/statuses/${statusId}`);
      await store.dispatch('fetchStatuses');
      Swal.fire(t('alerts.success'), t('messages.statusDeleted'), 'success');
    } catch (err) {
      Swal.fire(t('alerts.error'), err.response?.data?.message || t('alerts.error'), 'error');
    }
  }
}
</script>

<template>
  <div>
    <button class="btn btn-primary w-100 my-3" @click="openModal()">
      + {{ t('buttons.addStatus') }}
    </button>

    <table class="table table-bordered">
      <thead>
      <tr>
        <th>{{ t('tableheadings.name') }}</th>
        <th>{{ t('tableheadings.color') }}</th>
        <th>{{ t('tableheadings.role') }}</th>
        <th>{{ t('tableheadings.actions') }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="status in statuses" :key="status._id">
        <td>{{ status.name }}</td>
        <td>
          <div class="d-flex align-items-center">
            <span class="d-inline-block me-2" :style="{ backgroundColor: status.color, width: '20px', height: '20px', borderRadius: '4px' }"></span>
            {{ status.color }}
          </div>
        </td>
        <td>{{ status.role.name }}</td>
        <td>
          <button class="btn btn-sm btn-warning me-2" @click="openModal(status)">
            {{ t('buttons.edit') }}
          </button>
          <button class="btn btn-sm btn-danger" @click="deleteStatus(status._id)">
            {{ t('buttons.delete') }}
          </button>
        </td>
      </tr>
      </tbody>
    </table>

    <div class="modal fade" id="statusModal" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? t('modal.editStatus') : t('modal.addStatus') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{ t('labels.statusName') }}</label>
              <input type="text" class="form-control" v-model="activeStatus.name">
            </div>
            <div class="mb-3">
              <label class="form-label">{{ t('labels.statusColor') }}</label>
              <input type="color" class="form-control form-control-color" v-model="activeStatus.color">
            </div>
            <div class="mb-3">
              <label class="form-label">{{ t('labels.statusRole') }}</label>
              <select class="form-select" v-model="activeStatus.role">
                <option v-for="role in roles" :key="role._id" :value="role._id">
                  {{ role.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ t('buttons.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="saveStatus">{{ t('buttons.save') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>